import type { ChangeEvent, DragEvent } from "react";
import { useCallback, useRef, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Alert } from "../components/ui/Alert";
import { Breadcrumb } from "../components/ui/Breadcrumb";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import {
  useGeneratePresignedUrl,
  useLayout,
  useUpdateLayout,
} from "../hooks/api/useLayout";

export function AdminLayoutPage() {
  const { data: layout, isLoading } = useLayout();
  const generatePresignedUrl = useGeneratePresignedUrl();
  const updateLayout = useUpdateLayout();

  // アップロード状態
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewDimensions, setPreviewDimensions] = useState<{
    width: number;
    height: number;
    aspectRatio: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 許可される画像タイプ
  const allowedTypes = ["image/png", "image/jpeg", "image/webp"];

  // ファイル選択処理
  const handleFileSelect = useCallback((file: File) => {
    setUploadError(null);
    setUploadSuccess(false);

    if (!allowedTypes.includes(file.type)) {
      setUploadError("PNG、JPEG、またはWebP形式の画像を選択してください。");
      return;
    }

    // ファイルサイズチェック（10MB以下）
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("ファイルサイズは10MB以下にしてください。");
      return;
    }

    setSelectedFile(file);

    // プレビュー用URLを生成
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // 画像サイズを取得
    const img = new Image();
    img.onload = () => {
      setPreviewDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
        aspectRatio: img.naturalWidth / img.naturalHeight,
      });
    };
    img.src = objectUrl;
  }, []);

  // ドラッグ&ドロップ処理
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      const file = files[0];
      if (files.length > 0 && file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  // ファイル入力からの選択
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      const file = files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  // ファイル選択ダイアログを開く
  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  // 選択解除
  const handleClearSelection = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewDimensions(null);
    setUploadError(null);
    setUploadSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // アップロード処理
  const handleUpload = async () => {
    if (!(selectedFile && previewDimensions)) {
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      // 1. Presigned URLを取得
      const { presignedUrl, uploadPath } =
        await generatePresignedUrl.mutateAsync({
          fileName: selectedFile.name,
          contentType: selectedFile.type,
        });

      // 2. S3に直接アップロード
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("画像のアップロードに失敗しました。");
      }

      // 3. レイアウト情報を更新
      await updateLayout.mutateAsync({
        imageUrl: uploadPath,
        originalWidth: previewDimensions.width,
        originalHeight: previewDimensions.height,
        aspectRatio: previewDimensions.aspectRatio,
        fileName: selectedFile.name,
      });

      setUploadSuccess(true);
      handleClearSelection();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "アップロードに失敗しました。";
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  // アスペクト比変更の警告判定
  const hasAspectRatioChange =
    layout &&
    previewDimensions &&
    Math.abs(layout.aspectRatio - previewDimensions.aspectRatio) > 0.01;

  // ローディング
  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader />
        <PageContent>
          <div className="flex justify-center items-center py-12">
            <Spinner size="lg" />
            <span className="ml-3 text-gray-600">読み込み中...</span>
          </div>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <h1 className="sr-only">見取り図管理</h1>
        <Breadcrumb
          items={[
            { label: "管理", to: "/admin/layout" },
            { label: "見取り図管理" },
          ]}
        />
        <div className="space-y-6">
          {/* 成功メッセージ */}
          {uploadSuccess && (
            <Alert variant="success">見取り図を更新しました。</Alert>
          )}

          {/* 現在の見取り図 */}
          <Card>
            <CardHeader title="現在の見取り図" />
            <CardContent>
              {layout ? (
                <div className="space-y-4">
                  {/* プレビュー画像 */}
                  <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={layout.imageUrl}
                      alt="現在のオフィスレイアウト"
                      className="w-full max-h-96 object-contain"
                      data-testid="current-layout-image"
                    />
                  </div>

                  {/* 情報 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">サイズ</span>
                      <p
                        className="font-medium"
                        data-testid="layout-dimensions"
                      >
                        {layout.originalWidth} x {layout.originalHeight} px
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">アスペクト比</span>
                      <p
                        className="font-medium"
                        data-testid="layout-aspect-ratio"
                      >
                        {layout.aspectRatio.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">ファイル名</span>
                      <p
                        className="font-medium truncate"
                        title={layout.fileName}
                      >
                        {layout.fileName}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">更新日時</span>
                      <p className="font-medium">
                        {new Date(layout.updatedAt).toLocaleString("ja-JP")}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>見取り図が設定されていません。</p>
                  <p className="text-sm mt-2">
                    下のフォームから画像をアップロードしてください。
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* アップロードフォーム */}
          <Card>
            <CardHeader title="新しい見取り図をアップロード" />
            <CardContent>
              <div className="space-y-4">
                {/* エラーメッセージ */}
                {uploadError && <Alert variant="error">{uploadError}</Alert>}

                {/* ドラッグ&ドロップエリア or プレビュー */}
                {selectedFile ? (
                  /* プレビュー */
                  <div className="space-y-4">
                    {/* プレビュー画像 */}
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={previewUrl ?? ""}
                        alt="アップロードプレビュー"
                        className="w-full max-h-96 object-contain"
                        data-testid="upload-preview-image"
                      />
                    </div>

                    {/* 新しい画像情報 */}
                    {previewDimensions && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">ファイル名</span>
                          <p
                            className="font-medium truncate"
                            title={selectedFile.name}
                          >
                            {selectedFile.name}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">サイズ</span>
                          <p className="font-medium">
                            {previewDimensions.width} x{" "}
                            {previewDimensions.height} px
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">アスペクト比</span>
                          <p className="font-medium">
                            {previewDimensions.aspectRatio.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* アスペクト比変更警告 */}
                    {hasAspectRatioChange && (
                      <Alert
                        variant="warning"
                        data-testid="aspect-ratio-warning"
                      >
                        <strong>注意:</strong> アスペクト比が変更されます。
                        座席の配置が崩れる可能性があります。
                        <br />
                        <span className="text-sm">
                          現在: {layout?.aspectRatio.toFixed(2)} → 新規:{" "}
                          {previewDimensions?.aspectRatio.toFixed(2)}
                        </span>
                      </Alert>
                    )}

                    {/* ボタン */}
                    <div className="flex gap-3">
                      <Button
                        variant="secondary"
                        onClick={handleClearSelection}
                        disabled={isUploading}
                      >
                        キャンセル
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleUpload}
                        isLoading={isUploading}
                        data-testid="upload-button"
                      >
                        アップロード
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ドラッグ&ドロップエリア */
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleSelectClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSelectClick();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    data-testid="upload-dropzone"
                    className={`
                      border-2 border-dashed rounded-lg p-12
                      flex flex-col items-center justify-center
                      cursor-pointer transition-colors
                      ${
                        isDragging
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      }
                    `}
                  >
                    <svg
                      className="w-12 h-12 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="text-gray-600 mb-1">
                      ここに画像をドラッグ&ドロップ
                    </p>
                    <p className="text-sm text-gray-500">
                      または クリックしてファイルを選択
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      PNG、JPEG、WebP形式（10MB以下）
                    </p>
                  </div>
                )}

                {/* 隠しファイル入力 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleInputChange}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* 注意事項 */}
          <div className="text-sm text-gray-500 space-y-1">
            <p>
              <strong>注意:</strong>
            </p>
            <ul className="list-disc list-inside ml-2">
              <li>
                見取り図を変更すると、座席マップの背景画像が更新されます。
              </li>
              <li>
                座席の位置は相対座標（%）で保存されているため、
                同じアスペクト比の画像であれば座席位置は維持されます。
              </li>
              <li>
                アスペクト比が異なる画像に変更すると、座席の位置調整が必要になる場合があります。
              </li>
            </ul>
          </div>
        </div>
      </PageContent>
    </PageContainer>
  );
}
